import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { FlagIcon } from "../../constants/languageFlags";
import { MicIcon, CloseIcon, ArrowRightIcon, AlertCircleIcon } from "../../icons";
import { ENGINE_META, GENDER_META } from "../../constants";
import "./VoicePreferenceSummary.scss";

function VoicePreferenceSummary({
  open,
  onClose,
  onConfirm,
  onEditVoices,
  serviceLabel,
  languages,
  voices,
  loading = false,
  isSaving = false,
  saveError = "",
}) {
  const backdropRef = useRef(null);
  const confirmRef = useRef(null);

  /* ── Focus confirm button when modal opens ─────────────────────────────── */
  useEffect(() => {
    if (open) setTimeout(() => confirmRef.current?.focus(), 60);
  }, [open]);

  /* ── Escape key to close ───────────────────────────────────────────────── */
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  /* ── Lock body scroll ──────────────────────────────────────────────────── */
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open) return null;

  const handleBackdrop = (e) => {
    if (e.target === backdropRef.current) onClose();
  };

  const defaultCount = languages.filter((l) => voices[l]?.isDefault).length;
  const customCount = languages.filter((l) => voices[l] && !voices[l].isDefault).length;
  const missingCount = languages.filter((l) => !voices[l]).length;

  return (
    <div className="vps-backdrop" ref={backdropRef} onClick={handleBackdrop} role="presentation">
      <div className="vps-modal" role="dialog" aria-modal="true" aria-labelledby="vps-title">
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <header className="vps-header">
          <div className="vps-header-left">
            <div className="vps-header-icon">
              <MicIcon size={20} />
            </div>
            <div>
              <h2 id="vps-title" className="vps-title">
                Voice Preferences Summary
              </h2>
              <p className="vps-subtitle">
                Review assigned voices for&nbsp;<strong>{serviceLabel}</strong>
                &nbsp;before uploading
              </p>
            </div>
          </div>
          <button className="vps-close-btn" onClick={onClose} aria-label="Close">
            <CloseIcon size={14} />
          </button>
        </header>

        {/* ── Stats bar ───────────────────────────────────────────────────── */}
        <div className={`vps-stats${loading ? " vps-stats--hidden" : ""}`}>
          <div className="vps-stat">
            <span className="vps-stat-value vps-stat-value--custom">{customCount}</span>
            <span className="vps-stat-label">Custom</span>
          </div>
          <div className="vps-stat-divider" />
          <div className="vps-stat">
            <span className="vps-stat-value vps-stat-value--default">{defaultCount}</span>
            <span className="vps-stat-label">Auto-default</span>
          </div>
          <div className="vps-stat-divider" />
          <div className="vps-stat">
            <span
              className={`vps-stat-value${missingCount > 0 ? " vps-stat-value--missing" : " vps-stat-value--ok"}`}
            >
              {missingCount}
            </span>
            <span className="vps-stat-label">Unassigned</span>
          </div>
          <div className="vps-stat-divider" />
          <div className="vps-stat">
            <span className="vps-stat-value">{languages.length}</span>
            <span className="vps-stat-label">Total languages</span>
          </div>
        </div>

        {/* ── Info banner ─────────────────────────────────────────────────── */}
        {!loading && defaultCount > 0 && (
          <div className="vps-info-banner">
            <AlertCircleIcon size={14} />
            <span>
              <strong>
                {defaultCount} language{defaultCount > 1 ? "s" : ""}
              </strong>{" "}
              have been auto-assigned the first available Polly voice. You can customise them via
              &ldquo;Edit Voices&rdquo; or proceed with the defaults.
            </span>
          </div>
        )}

        {/* ── Voice table ─────────────────────────────────────────────────── */}
        <div className="vps-body">
          {loading && (
            <div className="vps-loading">
              <div className="vps-spinner" aria-label="Loading voices" />
              <p className="vps-loading-text">Fetching available voices…</p>
            </div>
          )}
          <div className={`vps-table-wrap${loading ? " vps-table-wrap--hidden" : ""}`}>
            <table className="vps-table" aria-label="Voice preferences by language">
              <thead>
                <tr>
                  <th>Language</th>
                  <th>Voice</th>
                  <th>Gender</th>
                  <th>Engine</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {languages.map((lang) => {
                  const voice = voices[lang];
                  const eng = voice ? ENGINE_META[voice.engine] || ENGINE_META.Standard : null;
                  const gen = voice ? GENDER_META[voice.gender] || GENDER_META.Male : null;
                  return (
                    <tr key={lang} className={!voice ? "vps-row--missing" : ""}>
                      <td>
                        <span className="vps-lang-cell">
                          <span className="vps-flag">
                            <FlagIcon language={lang} size={20} />
                          </span>
                          <span className="vps-lang-name">{lang}</span>
                        </span>
                      </td>
                      <td>
                        {voice ? (
                          <span className="vps-voice-name">{voice.name}</span>
                        ) : (
                          <span className="vps-unset">—</span>
                        )}
                      </td>
                      <td>
                        {voice && gen ? (
                          <span
                            className="vps-badge"
                            style={{
                              background: gen.bg,
                              color: gen.text,
                              borderColor: gen.border,
                            }}
                          >
                            {gen.symbol} {voice.gender}
                          </span>
                        ) : (
                          <span className="vps-unset">—</span>
                        )}
                      </td>
                      <td>
                        {voice && eng ? (
                          <span
                            className="vps-badge"
                            style={{
                              background: eng.bg,
                              color: eng.text,
                              borderColor: eng.border,
                            }}
                          >
                            {voice.engine}
                          </span>
                        ) : (
                          <span className="vps-unset">—</span>
                        )}
                      </td>
                      <td>
                        {voice ? (
                          voice.isDefault ? (
                            <span className="vps-source-badge vps-source-badge--default">
                              Auto-default
                            </span>
                          ) : (
                            <span className="vps-source-badge vps-source-badge--custom">
                              Custom
                            </span>
                          )
                        ) : (
                          <span className="vps-source-badge vps-source-badge--missing">
                            Unassigned
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Footer ──────────────────────────────────────────────────────── */}
        <footer className="vps-footer">
          <button type="button" className="vps-btn vps-btn--edit" onClick={onEditVoices}>
            Edit Voices
          </button>

          <div className="vps-footer-right">
            <button type="button" className="vps-btn vps-btn--cancel" onClick={onClose}>
              Back
            </button>
            <button
              ref={confirmRef}
              type="button"
              className={`vps-btn vps-btn--confirm${loading || isSaving ? " vps-btn--disabled" : ""}`}
              onClick={onConfirm}
              disabled={loading || isSaving}
            >
              {isSaving ? (
                <>
                  <span className="vps-spinner-inline" />
                  Saving…
                </>
              ) : (
                <>
                  <span>Confirm &amp; Continue</span>
                  <ArrowRightIcon size={15} />
                </>
              )}
            </button>
          </div>

          {/* ── Save error ── */}
          {saveError && (
            <div className="vps-save-error" role="alert">
              <AlertCircleIcon size={14} />
              {saveError}
            </div>
          )}
        </footer>
      </div>
    </div>
  );
}

VoicePreferenceSummary.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  onEditVoices: PropTypes.func,
  serviceLabel: PropTypes.string,
  languages: PropTypes.arrayOf(PropTypes.string),
  voices: PropTypes.objectOf(
    PropTypes.shape({
      name: PropTypes.string,
      engine: PropTypes.string,
      gender: PropTypes.string,
      isDefault: PropTypes.bool,
    })
  ),
  loading: PropTypes.bool,
  isSaving: PropTypes.bool,
  saveError: PropTypes.string,
};

export default VoicePreferenceSummary;
